/**
 * Boot: initialise MSAL, settle any sign-in redirect already in flight, and
 * only then render.
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { App } from './App'
import { ToastProvider } from './components/Toasts'
import './styles/app.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Config changes rarely and a stale list is misleading on a screen whose
      // whole job is telling you the current state — so refetch on focus, but
      // do not poll.
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status
        if (status && status >= 400 && status < 500) return false
        return failureCount < 2
      },
    },
  },
})

const root = createRoot(document.getElementById('root') as HTMLElement)

function renderApp(children: React.ReactNode) {
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>{children}</BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}

async function boot() {
  const [{ MsalProvider }, { msalInstance }, { AuthGate }] = await Promise.all([
    import('@azure/msal-react'),
    import('./auth/msal'),
    import('./auth/AuthGate'),
  ])

  await msalInstance.initialize()

  // Completes a sign-in that is mid-redirect. Must run before anything asks for
  // a token, or the first request races the account being set.
  const result = await msalInstance.handleRedirectPromise()
  if (result?.account) msalInstance.setActiveAccount(result.account)
  else if (!msalInstance.getActiveAccount()) {
    const [existing] = msalInstance.getAllAccounts()
    if (existing) msalInstance.setActiveAccount(existing)
  }

  renderApp(
    <MsalProvider instance={msalInstance}>
      <AuthGate>
        <App />
      </AuthGate>
    </MsalProvider>,
  )
}

void boot()
